import React, {useState} from 'react';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../../utils/APIClient';
import {API_URL} from '../../../../settings/local_settings';
import {isOk} from '../../../../utils/apiResponse';
import {Spinner} from '../../../Spinner';
import {X} from '../../../icons';
import {resizeAndCompress} from '../NotifyPaymentModal';
import {NS, formatAmount, liquidationAmountOf, periodLabelOf} from './subscriptionPageModel';
import {recordSubscriptionEvent} from './subscriptionRequests';

const HOW_OPTIONS = ['transfer', 'card', 'other'];

/**
 * «Avisar que ya pagaste» (mockup 5). Guarda lo mismo que `NotifyPaymentEndpoint` de siempre: el comentario,
 * el comprobante (captura pegada o link) y la factura. No hay campo de fecha (copy.md §10). «Cómo pagaste»
 * no tiene columna propia: viaja al principio del comentario, que es lo que lee el equipo.
 */
export function NotifyPaymentDialog({liquidation, onClose, onSent}) {
    const {t, i18n} = useTranslation();
    const lang = i18n && i18n.language;
    const [how, setHow] = useState('transfer');
    const [link, setLink] = useState('');
    const [image, setImage] = useState(null);
    const [comment, setComment] = useState('');
    const [sending, setSending] = useState(false);
    if (!liquidation) return null;

    const {amount, currency} = liquidationAmountOf(liquidation);
    const period = periodLabelOf(liquidation, lang);
    const subtitle = [period, formatAmount(amount, currency, lang)].filter(Boolean).join(' · ');
    // Mientras envía no se cierra: el aviso ya salió y la respuesta tiene que poder mostrarse.
    const close = () => { if (!sending) onClose(); };

    const onPaste = async (event) => {
        const items = (event.clipboardData && event.clipboardData.items) || [];
        for (let i = 0; i < items.length; i += 1) {
            if (items[i].type.indexOf('image') !== -1) {
                event.preventDefault();
                try {
                    setImage(await resizeAndCompress(items[i].getAsFile()));
                    setLink('');
                } catch (error) {
                    // Una captura que no se pudo leer se ignora: queda el link.
                }
                return;
            }
        }
    };

    const send = (event) => {
        event.preventDefault();
        setSending(true);
        const text = [t(`${NS}.notify.${how}`), comment.trim()].filter(Boolean).join(' · ');
        new APIClient(API_URL + 'agency/subscription/notify-payment/')
            .call('POST', {
                liquidation_id: liquidation.id,
                liquidation_month: liquidation.month_created || period,
                comments: text,
                attachment_url: image || link.trim() || undefined,
            })
            .then((response) => {
                setSending(false);
                if (isOk(response)) {
                    recordSubscriptionEvent('notify_payment');
                    toast.success(t(`${NS}.notify.sent`));
                    onSent();
                    return;
                }
                // Un gesto del usuario que falla: toast, nunca el `error_message` del backend.
                toast.error(t(`${NS}.notify.error`));
            });
    };

    return (
        <Modal isOpen toggle={close} className="sp-modal" labelledBy="sp-notify-title" scrollable>
            <form onSubmit={send}>
                <ModalHeader toggle={close} tag="h2" id="sp-notify-title">
                    {t(`${NS}.notify.title`)}
                    {subtitle && <span className="sp-modal__subtitle">{subtitle}</span>}
                </ModalHeader>
                <ModalBody>
                    <div className="sp-modal__body">
                        <fieldset className="sp-radios">
                            <legend>{t(`${NS}.notify.how`)}</legend>
                            {HOW_OPTIONS.map((option) => (
                                <label key={option} className={`sp-radio${how === option ? ' -on' : ''}`}>
                                    <input type="radio" name="sp-notify-how" value={option} checked={how === option}
                                           onChange={() => setHow(option)}/>
                                    {t(`${NS}.notify.${option}`)}
                                </label>
                            ))}
                        </fieldset>
                        <div className="sp-modal__field">
                            <label htmlFor="sp-notify-receipt">{t(`${NS}.notify.receipt`)}</label>
                            {image ? (
                                <div className="sp-receipt">
                                    <img src={image} alt={t(`${NS}.notify.receipt`)}/>
                                    <button type="button" className="sp-receipt__remove" onClick={() => setImage(null)}
                                            aria-label={t(`${NS}.notify.cancel`)}>
                                        <X size={14} aria-hidden="true"/>
                                    </button>
                                </div>
                            ) : (
                                <input id="sp-notify-receipt" className="sp-input" type="text" value={link}
                                       placeholder={t(`${NS}.notify.receipt_paste`)} onPaste={onPaste}
                                       onChange={(event) => setLink(event.target.value)}/>
                            )}
                        </div>
                        <div className="sp-modal__field">
                            <label htmlFor="sp-notify-comment">
                                {t(`${NS}.notify.comment`)} <span className="sp-muted">{t(`${NS}.confirm.optional`)}</span>
                            </label>
                            <textarea id="sp-notify-comment" className="sp-input" rows={3} value={comment}
                                      placeholder={t(`${NS}.notify.comment_placeholder`)}
                                      onChange={(event) => setComment(event.target.value)}/>
                        </div>
                        <p className="sp-modal__note">{t(`${NS}.notify.note`)}</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    {/* Mientras envía, el botón queda en su lugar (deshabilitado, con el spinner) y «Cancelar»
                        también se deshabilita: no hay nada que cancelar (QA-VIS-spfe-44). */}
                    <button type="button" className="btn -alt -negative" onClick={onClose} disabled={sending}>
                        {t(`${NS}.notify.cancel`)}
                    </button>
                    <button type="submit" className="btn -alt sp-btn-sending" disabled={sending} aria-busy={sending}>
                        {sending && <Spinner color="negative very-small"/>}
                        {t(`${NS}.notify.send`)}
                    </button>
                </ModalFooter>
            </form>
        </Modal>
    );
}
